/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useState, useEffect } from 'react';
import {
  User,
  Mail,
  Shield,
  Building2,
  BadgeCheck,
  Edit2,
  Save,
  X,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import toastService from '../utils/toastService';
import isEmail from 'validator/lib/isEmail';

const Profile = () => {
  const { updateProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    displayName: '',
    email: '',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get('/auth/me');
      setProfileData(data);
      setForm({
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        displayName: data.displayName || '',
        email: data.email || '',
      });
    } catch (err) {
      toastService.error(err.response?.data?.message || 'Failed to load profile details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] space-y-4">
        <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
        <p className="text-base-content/40 font-black uppercase tracking-widest text-xs">
          Loading Profile...
        </p>
      </div>
    );
  }

  if (!profileData) { return null; }

  const validate = () => {
    const newErrors = {};
    if (!form.firstName.trim()) { newErrors.firstName = 'First name is required'; }
    if (!form.lastName.trim()) { newErrors.lastName = 'Last name is required'; }
    if (!form.displayName.trim()) { newErrors.displayName = 'Display name is required'; }
    if (!form.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!isEmail(form.email)) {
      newErrors.email = 'Invalid email format';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBlur = (field, value) => {
    const newErrors = { ...errors };
    if (field === 'firstName') {
      value.trim() ? delete newErrors.firstName : (newErrors.firstName = 'First name is required');
    }
    if (field === 'lastName') {
      value.trim() ? delete newErrors.lastName : (newErrors.lastName = 'Last name is required');
    }
    if (field === 'displayName') {
      value.trim() ? delete newErrors.displayName : (newErrors.displayName = 'Display name is required');
    }
    setErrors(newErrors);
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      const updated = { ...errors };
      delete updated[field];
      setErrors(updated);
    }
  };

  const handleSave = async () => {
    if (!validate()) { return; }
    setSaving(true);
    try {
      const { data } = await apiClient.put('/auth/profile', { ...form });
      updateProfile(data);
      setProfileData(data);
      toastService.success('Profile updated successfully');
      setIsEditing(false);
    } catch (err) {
      toastService.error(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm({
      firstName: profileData.firstName || '',
      lastName: profileData.lastName || '',
      displayName: profileData.displayName || '',
      email: profileData.email || '',
    });
    setErrors({});
    setIsEditing(false);
  };

  const getRoleBadgeColor = (role) => {
    if (role?.includes('platform')) { return 'bg-rose-500/10 text-rose-500 border-rose-500/20'; }
    if (role?.includes('account')) { return 'bg-blue-500/10 text-blue-500 border-blue-500/20'; }
    return 'bg-primary/10 text-primary border-primary/20';
  };

  const labelCls = 'text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] mb-2 flex items-center gap-2 px-1';

  const inputCls = (field, disabled = false) =>
    `w-full px-4 py-3 border rounded-xl text-sm font-medium transition-all outline-none ` +
    (disabled
      ? 'bg-base-200/50 border-base-content/5 text-base-content/50 opacity-60 cursor-not-allowed'
      : isEditing
        ? errors[field]
          ? 'bg-base-100 border-error focus:ring-2 focus:ring-error/20'
          : 'bg-base-100 border-base-300 focus:ring-2 focus:ring-primary/20 focus:border-primary'
        : 'bg-base-100 border-base-content/5 text-base-content/70 cursor-default');

  return (
    <div className="mx-auto w-full space-y-6 pb-12">

      {/* Header */}
      <div className="flex flex-col gap-4 px-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="page-title">My Profile</h1>
          <p className="page-subtitle">Personal information and authorization context</p>
        </div>

        <div className="flex items-center gap-3">
          {isEditing ? (
            <>
              <button
                onClick={handleCancel}
                className="flex items-center gap-2 rounded-xl border border-base-content/10 bg-base-100 px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-base-content/60 hover:bg-base-200 transition-all active:scale-95"
              >
                <X size={14} />
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn-premium flex items-center gap-2 px-6 py-2.5 text-xs"
              >
                {saving
                  ? <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  : <Save size={14} />
                }
                Save Changes
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="btn-premium flex items-center gap-2 px-6 py-2.5 text-xs"
            >
              <Edit2 size={14} />
              Edit Profile
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">

        {/* Personal Information Card */}
        <div className="rounded-xl border border-base-content/5 bg-base-100/90 shadow-sm p-5 space-y-6">
          <div>
            <h3 className="font-black text-base-content text-lg mb-1 tracking-tight">Personal Information</h3>
            <p className="text-sm font-medium text-base-content/45">Basic user information and profile identity.</p>
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">

            {/* First Name */}
            <div className="space-y-1">
              <label className={labelCls}>
                <User size={10} className="text-primary/60" />
                First Name {isEditing && '*'}
              </label>
              <div className="relative">
                <input
                  autoComplete="off"
                  type="text"
                  value={form.firstName}
                  readOnly={!isEditing}
                  maxLength={30}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  onBlur={(e) => isEditing && handleBlur('firstName', e.target.value)}
                  placeholder={isEditing ? 'Enter first name' : ''}
                  className={inputCls('firstName')}
                />
                {isEditing && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[9px] font-bold text-base-content/20 uppercase tracking-widest">
                    {form.firstName.length}/30
                  </span>
                )}
              </div>
              {isEditing && errors.firstName && (
                <p className="text-error text-xs mt-1 font-medium">{errors.firstName}</p>
              )}
            </div>

            {/* Last Name */}
            <div className="space-y-1">
              <label className={labelCls}>
                <User size={10} className="text-primary/60" />
                Last Name {isEditing && '*'}
              </label>
              <div className="relative">
                <input
                  autoComplete="off"
                  type="text"
                  value={form.lastName}
                  readOnly={!isEditing}
                  maxLength={30}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  onBlur={(e) => isEditing && handleBlur('lastName', e.target.value)}
                  placeholder={isEditing ? 'Enter last name' : ''}
                  className={inputCls('lastName')}
                />
                {isEditing && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[9px] font-bold text-base-content/20 uppercase tracking-widest">
                    {form.lastName.length}/30
                  </span>
                )}
              </div>
              {isEditing && errors.lastName && (
                <p className="text-error text-xs mt-1 font-medium">{errors.lastName}</p>
              )}
            </div>

            {/* Display Name */}
            <div className="space-y-1">
              <label className={labelCls}>
                <BadgeCheck size={10} className="text-primary/60" />
                Display Name {isEditing && '*'}
              </label>
              <div className="relative">
                <input
                  autoComplete="off"
                  type="text"
                  value={form.displayName}
                  readOnly={!isEditing}
                  maxLength={30}
                  onChange={(e) => handleChange('displayName', e.target.value)}
                  onBlur={(e) => isEditing && handleBlur('displayName', e.target.value)}
                  placeholder={isEditing ? 'Enter display name' : ''}
                  className={inputCls('displayName')}
                />
                {isEditing && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[9px] font-bold text-base-content/20 uppercase tracking-widest">
                    {form.displayName.length}/30
                  </span>
                )}
              </div>
              {isEditing && errors.displayName && (
                <p className="text-error text-xs mt-1 font-medium">{errors.displayName}</p>
              )}
            </div>

            {/* Email — always read-only */}
            <div className="space-y-1">
              <label className={labelCls}>
                <Mail size={10} className="text-primary/60" />
                Email Address
              </label>
              <input
                type="email"
                value={form.email}
                readOnly
                disabled
                className={inputCls('email', true)}
              />
            </div>

          </div>
        </div>

        {/* Account Access Card */}
        <div className="rounded-xl border border-base-content/5 bg-base-100/90 shadow-sm p-5 space-y-6">
          <div>
            <h3 className="font-black text-base-content text-lg mb-1 tracking-tight">Account Access</h3>
            <p className="text-sm font-medium text-base-content/45">Access scope and assigned authorization details.</p>
          </div>

          <div className="space-y-5">

            {/* Role Badge */}
            <div className="space-y-1 flex items-center space-between w-100 justify-between">
              <label className={labelCls}>
                <Shield size={10} className="text-primary/60" />
                Assigned Role
              </label>
              <div className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-[11px] font-semibold uppercase tracking-wide ${getRoleBadgeColor(profileData.role)}`}>
                {profileData.role?.replace('_', ' ')}
              </div>
            </div>

            {/* Account */}
            {profileData.accountName && (
              <div className="space-y-1 flex items-center space-between w-100 justify-between">
                <label className={labelCls}>
                  <Building2 size={10} className="text-primary/60" />
                  Account
                </label>
                <p
                  className="w-auto px-4 py-3 bg-base-100  text-sm font-medium text-base-content/70"
                >
                  {profileData.accountName}
                </p>
              </div>
            )}

            {/* Sub Account */}
            {profileData.subAccountName && (
              <div className="space-y-1 flex items-center space-between w-100 justify-between">
                <label className={labelCls}>
                  <Building2 size={10} className="text-primary/60" />
                  Sub Account
                </label>
                <p
                  className="w-auto px-4 py-3 bg-base-100  text-sm font-medium text-base-content/70"
                >
                  {profileData.subAccountName}
                </p>
              </div>
            )}

          </div>
        </div>

      </div>
    </div>
  );
};

export default Profile;